<!-- eslint-disable vue/no-deprecated-v-bind-sync -->
<template>
  <div>
    <!-- Linear loading -->
    <v-progress-linear v-if="loading" absolute height="6" color="grey" :indeterminate="true" />

    <!-- Didn't found content -->
    <v-alert v-if="notFound" type="error" class="mt-6">
      <div class="mb-2">
        <strong>Ups</strong>, looks something is not ok!<br>
        Please try again, if the problem persists make sure that this item exists and your connection.<br>
      </div>
      <v-btn outlined class="mr-2" @click="router.push({name: 'home'})">
        <v-icon>home</v-icon>
        <span class="ml-1">Back to Home</span>
      </v-btn>
    </v-alert>

    <!-- Workspace not selected -->
    <v-alert v-if="differentWorkspace" type="error" class="mt-6">
      <div class="mb-2">
        Content doesn't belong to "{{ store.getters.workspace.name }}" workspace! <br>
      </div>
      <v-btn outlined class="mr-2" @click="router.push({name: 'home'})">
        <v-icon>home</v-icon>
        <span class="ml-1">Back to Home</span>
      </v-btn>
      <v-btn outlined class="mr-2" @click="switchWorkspace(content.workspace)">
        <v-icon>work_outline</v-icon>
        <span class="ml-1">Change Workspace</span>
      </v-btn>
    </v-alert>

    <!-- Doesn't have permissions or the Content can't be create in this workspace -->
    <v-alert v-if="disallowedContentType" type="error" class="mt-6">
      <div class="mb-2">
        Can't create a {{ newContentTitle }} content <br>
      </div>
      <v-btn outlined class="mr-2" @click="router.push({ name: 'home' })">
        <v-icon>home</v-icon>
        <span class="ml-1">Back to Home</span>
      </v-btn>
    </v-alert>

    <div v-if="!differentWorkspace && !disallowedContentType">
      <v-row v-if="!loading && content" flat>
        <v-col col="12" class="d-flex align-center floating-bar">
          <!-- Go back button -->
          <a class="cursor-as-pointer" tag="button" @click="goBack">
            <v-btn icon>
              <v-icon>chevron_left</v-icon>
            </v-btn>
          </a>

          <Breadcrumbs :path="content.path" :file-name="fileName" />

          <!-- Status of the content type -->
          <StatusDisplay
            v-if="contentType.configs.workflow"
            class="ml-5 py-2"
            :status="content.status"
            :states="workflowStatus"
          />
          <v-spacer />

          <v-btn icon @click="goToContentEditor()">
            <v-icon>build</v-icon>
          </v-btn>

          <!-- Create or workflow buttons -->
          <div v-if="store.getters.permissionToWrite">
            <!-- TODO save function -->
            <v-btn light class="mx-2" @click="save()">Save</v-btn>
          </div>
        </v-col>
      </v-row>

      <SeoInlineEditor
        :content-valid.sync="contentValid"
        :content.sync="content"
        :seo-content.sync="seoContent"
        :content-type="contentType"
        :loading.sync="loading "
        :seo-preview-workspaces-list="seoPreviewWorkspacesList"
        :seo-content-workspace.sync="seoContentWorkspace"
      />
    </div>
  </div>
</template>

<script setup>

import { ref, computed, watch } from 'vue';

import { useRoute, useRouter } from 'vue2-helpers/vue-router';
import { useStore } from 'vue2-helpers/vuex';
import { useQuery, useMutation } from '@vue/apollo-composable';

import _ from 'lodash';
import gql from 'graphql-tag';

import { getGraphqlSchema } from '@/graphql/index';

import { getContentType, getTitleField, setDefaultValues } from '@/utils/content';
import { buildContentQuery } from '@/utils/editor';

import Breadcrumbs from '../components/editor/Breadcrumbs.vue';
import StatusDisplay from '../components/editor/StatusDisplay.vue';
import SeoInlineEditor from '../components/editor/SeoInlineEditor.vue';

const { allowedContentTypes } = require('../utils/permissions');
const { getValidContentTypes } = require('../utils/content');

const store = useStore();
const router = useRouter();
const route = useRoute();

const loading = ref(true);

const goBack = () => {
  router.go(-1);
};

/** ******************
COMMON CONTENT
******************* */

const content = ref(null);

const seoContent = ref({});

const originalSeoContent = ref({ ...seoContent.value });

const wtype = computed(() => route.params.wtype);

// Current contentType Workspace
const workspaceCode = computed(() => store.getters.workspace.code);

const contentType = computed(() => getContentType(wtype.value, store.state.rawContentTypes));

const seoPreviewWorkspacesList = ref(null);

// Workspace of the seoContent that is being edited
const seoContentWorkspace = ref(store.getters.workspace.code);

const contentValid = ref(false);

const mutationErrorMessages = ref([]);
const mutationSuccessMessage = ref(null);

const writeErrorMsg = msg => {
  if (!msg) {
    return;
  } if (!Array.isArray(msg)) {
    msg = [msg];
  }

  msg.forEach(error => {
    // if is single value
    if (typeof error === 'string') {
      mutationErrorMessages.value.push({ type: 'error', message: error });
    } else {
      mutationErrorMessages.value.push(error);
    }
  });

  // if (msg) { this.loading = false; }
};

const showSuccessMessage = msg => {
  mutationSuccessMessage.value = msg;
  if (msg) { setTimeout(callback => { callback(null); }, 7000, showSuccessMessage); }
};

const switchWorkspace = workspace => {
  const workspaceObject = store.getters.workspaces.find(w => w.code === workspace);
  if (workspaceObject) {
    store.commit('setWorkspace', workspaceObject);
  }
};

const goToContentEditor = () => {
  router.push({ name: 'ContentEditor', params: { wtype: route.params.wtype, uuid: route.params.uuid } });
};

// store.commit('setHasChanges', false);

// watch(seoContent, newVal => {
//   const hasChanges = !_.isEqual(originalSeoContent.value, newVal);
//   store.commit('setHasChanges', hasChanges);
// }, { deep: true });

/** *******************
EDIT SEOCONTENT
******************* */

// If content not found
const notFound = computed(() => !loading.value && !content.value);

const differentWorkspace = computed(() => !notFound.value && !loading.value && content.value && content.value.workspace !== workspaceCode.value);

const originalContent = ref(setDefaultValues({}, contentType.value));

const fileName = computed(() => {
  const titleFieldName = getTitleField(contentType.value);
  return originalContent.value[titleFieldName];
});

const disallowedContentType = computed(() => {
  // Can't create content types that are not translated in the current locale or don't belong to the current workspace
  let contentTypesList = getValidContentTypes(store.state.rawContentTypes, store.getters.workspace, store.getters.locale);

  contentTypesList = contentTypesList.filter(a => a !== undefined);

  const { currentUser } = store.state;
  const currentWorkspace = store.getters.workspace;
  const currentProject = store.state.environment.PROJECT;

  // Check if the User has content type permission
  contentTypesList = allowedContentTypes(contentTypesList, currentUser.restrictions, currentWorkspace.code, currentProject);

  return !loading.value && !contentTypesList.find(ct => ct.configs?.wtype === wtype.value);
});

const workflowStatus = computed(() => store.state.workflows[contentType.value.configs.workflow].states);

/** ************** GET INITIAL INFORMATION ************* */

// Get Agencies
const agenciesQuery = computed(() => getGraphqlSchema('agencies'));

const loadingAgenciesQuery = ref(true);

const { onResult: onResultAgencies } = useQuery(
  agenciesQuery,
  () => ({ fetchPolicy: 'no-cache', notifyOnNetworkStatusChange: true })
);

onResultAgencies(result => {
  if (result.loading) {
    loadingAgenciesQuery.value = true;

    mutationErrorMessages.value = [];
    return;
  }
  const agencies = result.data?.agencies?.items;

  if (agencies) {
    // Only agencies that show the content from this workspace
    const workspaceProductsAgencies = agencies.filter(a => a.productsWorkspace === workspaceCode.value);

    seoPreviewWorkspacesList.value = store.getters.workspaces.filter(w => workspaceProductsAgencies.find(a => a.pageWorkspace && a.pageWorkspace === w.code));
  }

  loadingAgenciesQuery.value = false;
});

// Get normal Content
const query = computed(() => gql(buildContentQuery(store.state.rawContentTypes, contentType.value)));

const variablesQuery = computed(() => ({ uuid: route.params.uuid }));

const loadingContentQuery = ref(true);

const { onResult: onResultContent } = useQuery(
  query,
  variablesQuery,
  () => ({ fetchPolicy: 'no-cache', notifyOnNetworkStatusChange: true })
);

onResultContent(result => {
  if (result.loading) {
    loadingContentQuery.value = true;
    content.value = null;

    mutationErrorMessages.value = [];
    return;
  }

  const contentResponse = result.data[contentType.value.configs.single];

  if (contentResponse) {
    content.value = setDefaultValues({ ...contentResponse }, contentType.value);
    originalContent.value = setDefaultValues({ ...contentResponse }, contentType.value);
  }

  loadingContentQuery.value = false;
});

// Get SEO Content

const seoContentQuery = getGraphqlSchema('seoContent');

const seoContentQueryVariables = computed(() => ({ uuid: route.params.uuid, workspace: seoContentWorkspace.value }));

const loadingSeoContentQuery = ref(true);

const { onResult: onResultSeoContent } = useQuery(
  seoContentQuery,
  seoContentQueryVariables,
  () => ({ enabled: true, fetchPolicy: 'no-cache', notifyOnNetworkStatusChange: true })
);

onResultSeoContent(result => {
  if (result.loading) {
    loadingSeoContentQuery.value = true;
    return;
  }

  seoContent.value = result.data.seoContent;
  originalSeoContent.value = { ...result.data.seoContent };

  loadingSeoContentQuery.value = false;
});

/** ********************************************** */

/** ************** Save SEO Content ************* */

const mutationUpdate = getGraphqlSchema('updateSeoContent');

const variablesUpdate = computed(() => ({
  uuid: route.params.uuid,
  workspace: seoContentWorkspace.value,
  content: seoContent.value
}));

const { mutate: mutationUpdateSeoContent, onDone: onDoneUpdate, onError: onErrorUpdate } = useMutation(mutationUpdate, () => ({ variables: variablesUpdate.value }));

onErrorUpdate(error => {
  loading.value = false;

  if (error.message) {
    writeErrorMsg(error.message);
  }
});

onDoneUpdate(r => {
  loading.value = false;

  mutationErrorMessages.value = [];
  showSuccessMessage('SEO Content updated.');
});

function save () {
  loading.value = true;
  mutationErrorMessages.value = [];

  mutationUpdateSeoContent();
}

watch(
  [loadingContentQuery, loadingAgenciesQuery, loadingSeoContentQuery],
  ([isContentLoading, isAgenciesLoading, isSeoContentLoading]) => {
    loading.value = isContentLoading || isAgenciesLoading || isSeoContentLoading;
  }
);

</script>
